import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import {
  ArrowRight,
  Download,
  Loader2,
  LogOut,
  RefreshCw,
  Search,
  ShieldCheck,
  Phone,
  Mail,
  MessageCircle,
  Lock,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "sge_admin_token";
const HTTP_UNAUTHORIZED = 401;
const HTTP_SERVICE_UNAVAILABLE = 503;

export default function AdminLeads() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");

  useEffect(() => {
    document.title = "Admin · Leads — Shri Gurunath Enterprises";
  }, []);

  const fetchAll = useCallback(async (t) => {
    setLoading(true);
    setError("");
    try {
      const headers = { "X-Admin-Password": t };
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/leads`, { headers }),
        axios.get(`${API}/admin/leads/stats`, { headers }),
      ]);
      setLeads(leadsRes.data || []);
      setStats(statsRes.data || null);
      setAuthed(true);
    } catch (e) {
      const status = e?.response?.status;
      if (status === HTTP_UNAUTHORIZED) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setAuthed(false);
        setAuthError("Session expired. Please sign in again.");
      } else {
        setError(e?.response?.data?.detail || "Failed to load leads. Please retry.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) fetchAll(token);
  }, [token, fetchAll]);

  const onLogin = async (e) => {
    e.preventDefault();
    setAuthError("");
    if (!password.trim()) {
      setAuthError("Please enter the admin password.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password: password.trim() });
      const t = res.data?.token;
      if (!t) throw new Error("No token returned");
      localStorage.setItem(TOKEN_KEY, t);
      setToken(t);
      setPassword("");
    } catch (err) {
      const status = err?.response?.status;
      if (status === HTTP_UNAUTHORIZED) setAuthError("Incorrect password.");
      else if (status === HTTP_SERVICE_UNAVAILABLE) setAuthError("Admin access is not configured on the server.");
      else setAuthError(err?.response?.data?.detail || "Login failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setLeads([]);
    setStats(null);
  };

  const exportCsv = () => {
    if (!filtered.length) return;
    const rows = filtered.map((l) => ({
      "Received": formatDate(l.created_at),
      "Name": l.name || "",
      "Mobile": l.mobile || "",
      "Email": l.email || "",
      "Project Type": l.project_type || "",
      "Product Category": l.product_category || "",
      "Product Interest": Array.isArray(l.product_interest) ? l.product_interest.join("; ") : "",
      "Quantity (Sq. Ft.)": l.quantity || "",
      "Message": l.message || "",
      "Source": l.source || "",
      "Email Notification": l.email_sent ? "Sent" : "Not sent",
    }));
    const headers = Object.keys(rows[0]);
    const escape = (v) => {
      const s = String(v ?? "");
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [
      headers.join(","),
      ...rows.map((r) => headers.map((h) => escape(r[h])).join(",")),
    ].join("\r\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `sge-leads-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const categories = useMemo(() => {
    const set = new Set(leads.map((l) => l.product_category).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [leads]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return leads.filter((l) => {
      const inCat = category === "All" || l.product_category === category;
      const inQuery =
        !q ||
        l.name?.toLowerCase().includes(q) ||
        l.mobile?.toLowerCase().includes(q) ||
        l.product_category?.toLowerCase().includes(q) ||
        l.quantity?.toLowerCase().includes(q) ||
        l.message?.toLowerCase().includes(q);
      return inCat && inQuery;
    });
  }, [leads, query, category]);

  if (!authed) {
    return <LoginGate password={password} setPassword={setPassword} onSubmit={onLogin} error={authError} submitting={submitting} />;
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5] text-slate-900" data-testid="admin-leads-page">
      {/* Header */}
      <header className="bg-slate-950 text-white">
        <div className="mx-auto max-w-7xl px-5 lg:px-10 py-7 flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="eyebrow !text-amber-500">Owner Console</p>
            <h1 className="font-display text-3xl md:text-4xl mt-1">Wholesale Inquiry Leads</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={exportCsv}
              disabled={!filtered.length}
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-3 text-[11px] tracking-[0.22em] uppercase border border-emerald-500"
              data-testid="admin-export-csv-button"
              title={filtered.length ? `Download ${filtered.length} leads as CSV` : "No leads to export"}
            >
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">Export CSV</span>
              <span className="sm:hidden">CSV</span>
              {filtered.length > 0 && (
                <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded-sm">{filtered.length}</span>
              )}
            </button>
            <button
              onClick={() => fetchAll(token)}
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 text-white px-4 py-3 text-[11px] tracking-[0.22em] uppercase border border-white/15"
              data-testid="admin-refresh-button"
              disabled={loading}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Refresh
            </button>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 bg-amber-700 hover:bg-amber-800 text-white px-4 py-3 text-[11px] tracking-[0.22em] uppercase"
              data-testid="admin-logout-button"
            >
              <LogOut className="h-4 w-4" /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 lg:px-10 py-10 space-y-10">
        {/* Stats */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats-row">
          <StatCard label="Total Leads" value={stats?.total ?? "—"} />
          <StatCard label="Email Notifications Sent" value={stats?.emailed ?? "—"} hint={stats && stats.total ? `${Math.round(((stats.emailed || 0) / Math.max(stats.total, 1)) * 100)}% delivered` : ""} />
          <StatCard label="Top Category" value={stats?.by_category?.[0]?.category || "—"} hint={stats?.by_category?.[0] ? `${stats.by_category[0].count} leads` : ""} />
          <StatCard label="Categories Covered" value={stats?.by_category?.length ?? "—"} />
        </section>

        {/* Filters */}
        <section className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, mobile, category, quantity or message…"
              className="w-full bg-white border border-slate-200 focus:border-amber-700 outline-none pl-11 pr-4 py-3 text-sm"
              data-testid="admin-search-input"
            />
          </div>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="bg-white border border-slate-200 focus:border-amber-700 outline-none px-4 py-3 text-sm min-w-[220px]"
            data-testid="admin-category-filter"
          >
            {categories.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </section>

        {error && (
          <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3" data-testid="admin-error">
            <AlertTriangle className="h-4 w-4 mt-[2px]" /> {error}
          </div>
        )}

        {/* Leads list */}
        <section className="bg-white border border-slate-200" data-testid="admin-leads-table">
          <div className="hidden md:grid grid-cols-12 px-5 py-4 bg-slate-50 border-b border-slate-200 text-[11px] tracking-[0.22em] uppercase text-slate-500">
            <div className="col-span-3">Name · Mobile</div>
            <div className="col-span-3">Category</div>
            <div className="col-span-2">Quantity</div>
            <div className="col-span-2">Received</div>
            <div className="col-span-2 text-right">Email</div>
          </div>

          <LeadsTableBody loading={loading} filtered={filtered} />
        </section>

        <p className="text-xs text-slate-500">Showing {filtered.length} of {leads.length} leads. Latest inquiries first.</p>
      </main>
    </div>
  );
}

function StatCard({ label, value, hint }) {
  return (
    <div className="bg-white border border-slate-200 p-5">
      <p className="eyebrow !text-slate-500">{label}</p>
      <p className="font-display text-3xl md:text-4xl text-slate-900 mt-2 leading-none">{value}</p>
      {hint && <p className="mt-2 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

function LeadsTableBody({ loading, filtered }) {
  if (loading) {
    return (
      <div className="px-5 py-12 text-center text-slate-500 flex items-center justify-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading leads…
      </div>
    );
  }
  if (filtered.length === 0) {
    return (
      <div className="px-5 py-16 text-center text-slate-500" data-testid="admin-empty-state">
        <p className="font-display text-2xl text-slate-900">No leads match your filters.</p>
        <p className="mt-2 text-sm">Try clearing search or category to see all inquiries.</p>
      </div>
    );
  }
  return (
    <ul className="divide-y divide-slate-200">
      {filtered.map((l) => (
        <LeadRow key={l.id} lead={l} />
      ))}
    </ul>
  );
}

function LeadRow({ lead: l }) {
  return (
    <li className="grid grid-cols-1 md:grid-cols-12 gap-3 px-5 py-5 hover:bg-slate-50/60 transition-colors" data-testid={`admin-lead-row-${l.id}`}>
      <div className="md:col-span-3">
        <p className="font-display text-lg text-slate-900">{l.name}</p>
        <p className="text-sm text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
          <a href={`tel:${l.mobile}`} className="inline-flex items-center gap-1 hover:text-amber-700">
            <Phone className="h-3.5 w-3.5" /> {l.mobile}
          </a>
          <a
            href={`https://wa.me/${(l.mobile || "").replace(/[^0-9]/g, "")}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700"
          >
            <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
          </a>
        </p>
      </div>
      <div className="md:col-span-3 text-sm">
        <span className="inline-block bg-amber-700/10 border border-amber-700/30 text-amber-800 px-2 py-1 text-[11px] tracking-[0.15em] uppercase">{l.product_category}</span>
        {l.message && <p className="mt-2 text-slate-600 line-clamp-3">{l.message}</p>}
      </div>
      <div className="md:col-span-2 text-sm text-slate-700">{l.quantity}</div>
      <div className="md:col-span-2 text-sm text-slate-500">{formatDate(l.created_at)}</div>
      <div className="md:col-span-2 md:text-right">
        {l.email_sent ? (
          <span className="inline-flex items-center gap-1 text-emerald-700 text-xs">
            <CheckCircle2 className="h-3.5 w-3.5" /> Sent
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-slate-400 text-xs" title="Email skipped — Resend not configured">
            <Mail className="h-3.5 w-3.5" /> Not sent
          </span>
        )}
      </div>
    </li>
  );
}

function LoginGate({ password, setPassword, onSubmit, error, submitting }) {
  return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-5" data-testid="admin-login-gate">
      <div className="absolute inset-0 bg-grain opacity-30 pointer-events-none" />
      <div className="relative w-full max-w-md bg-white text-slate-900 border border-white/10 p-8 md:p-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="inline-flex h-11 w-11 items-center justify-center bg-amber-700/10 border border-amber-700/40 text-amber-700">
            <ShieldCheck className="h-5 w-5" />
          </span>
          <div>
            <p className="eyebrow">Owner Console</p>
            <h1 className="font-display text-2xl text-slate-900 leading-tight">Sign in to view leads</h1>
          </div>
        </div>
        <form onSubmit={onSubmit} className="space-y-5">
          <div>
            <label className="eyebrow !text-slate-500 mb-2 block">Admin Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white border border-slate-300 focus:border-amber-700 outline-none pl-10 pr-3 py-3 text-sm"
                placeholder="Enter the password set in backend/.env"
                data-testid="admin-password-input"
              />
            </div>
          </div>
          {error && <p className="text-sm text-red-600" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit"
            disabled={submitting}
            className="w-full glow-amber bg-amber-700 hover:bg-amber-800 disabled:opacity-70 text-white px-6 py-3.5 text-[12px] tracking-[0.24em] uppercase inline-flex items-center justify-center gap-2"
            data-testid="admin-login-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            Sign In
          </button>
        </form>
        <p className="mt-6 text-xs text-slate-500 leading-relaxed">
          Protected by a shared owner password configured in <code className="bg-slate-100 px-1">ADMIN_PASSWORD</code>. Update it any time from <code className="bg-slate-100 px-1">/app/backend/.env</code> and restart the backend.
        </p>
      </div>
    </div>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso || "—";
  }
}
